import React, { useContext, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Link,
	List,
	ListItemButton,
	ListItemText,
	Typography,
} from '@mui/material';
import AuthContext from '../../store/auth-context';
import { fetchStudentOpinionBlob } from '../../api/user-api';
import Student from '../../model/Student';

type OpinionFile = NonNullable<Student['opinionScan']>['files'][number];

type Props = {
	student: Student | null | undefined;
	/** Inline „Podgląd” (lista) albo większy przycisk w edycji. */
	variant?: 'link' | 'button';
};

/**
 * Podgląd skanu opinii: lista załączników (nazwy z meta), domyślnie pierwszy w iframe.
 */
const OpinionScanPreview = ({ student, variant = 'link' }: Props) => {
	const { token } = useContext(AuthContext);
	const [open, setOpen] = useState(false);
	const [activeId, setActiveId] = useState<number | null>(null);
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const files = student?.opinionScan?.files ?? [];

	useEffect(() => {
		return () => {
			if (blobUrl) {
				URL.revokeObjectURL(blobUrl);
			}
		};
	}, [blobUrl]);

	if (!student?.opinionScan?.hasFile || files.length === 0) {
		return null;
	}

	const active: OpinionFile = files.find((f) => f.id === activeId) ?? files[0];

	const loadFile = async (file: OpinionFile) => {
		setActiveId(file.id);
		setError('');
		if (file.previewUrl) {
			setBlobUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return null;
			});
			return;
		}
		if (!token) {
			setError('Brak sesji — zaloguj się ponownie.');
			return;
		}
		setLoading(true);
		try {
			const blob = await fetchStudentOpinionBlob(token, student.id, file.id > 0 ? file.id : undefined);
			const url = URL.createObjectURL(blob);
			setBlobUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return url;
			});
		} catch {
			setError('Nie udało się pobrać skanu.');
		} finally {
			setLoading(false);
		}
	};

	const openPreview = async () => {
		setOpen(true);
		await loadFile(files[0]);
	};

	const trigger =
		variant === 'button' ? (
			<Button
				size='small'
				variant='outlined'
				onClick={openPreview}
			>
				Podgląd skanu{files.length > 1 ? ` (${files.length})` : ''}
			</Button>
		) : (
			<Link
				component='button'
				type='button'
				variant='caption'
				fontWeight='medium'
				onClick={openPreview}
				sx={{ cursor: 'pointer' }}
			>
				Podgląd{files.length > 1 ? ` (${files.length})` : ''}
			</Link>
		);

	const iframeSrc = active?.previewUrl || blobUrl;

	return (
		<>
			{trigger}
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				fullWidth
				maxWidth='lg'
			>
				<DialogTitle>
					Skan opinii — {student.firstName} {student.lastName}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, minHeight: '60vh' }}>
						<Box sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
							<Typography
								variant='subtitle2'
								gutterBottom
							>
								Załączniki ({files.length})
							</Typography>
							<List
								dense
								disablePadding
							>
								{files.map((f, idx) => (
									<ListItemButton
										key={f.id || idx}
										selected={(activeId ?? files[0].id) === f.id}
										onClick={() => loadFile(f)}
									>
										<ListItemText
											primary={f.originalName || f.storedName || `Plik ${idx + 1}`}
											secondary={f.storedName && f.originalName !== f.storedName ? f.storedName : undefined}
											primaryTypographyProps={{ variant: 'body2' }}
											secondaryTypographyProps={{ variant: 'caption' }}
										/>
									</ListItemButton>
								))}
							</List>
						</Box>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							{loading && <Typography variant='body2'>Ładowanie…</Typography>}
							{error && (
								<Typography
									variant='body2'
									color='error'
								>
									{error}
								</Typography>
							)}
							{iframeSrc && !loading && (
								<Box sx={{ height: { xs: '50vh', sm: '70vh' } }}>
									<iframe
										title='Podglad skanu opinii'
										src={iframeSrc}
										style={{ width: '100%', height: '100%', border: 0 }}
									/>
								</Box>
							)}
							{active?.previewUrl && (
								<Typography
									variant='caption'
									sx={{ display: 'block', mt: 1 }}
								>
									Podgląd Google Drive.{' '}
									<Link
										href={active.previewUrl}
										target='_blank'
										rel='noopener noreferrer'
									>
										Otwórz w nowej karcie
									</Link>
								</Typography>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Zamknij</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default OpinionScanPreview;
